/* eslint-disable react/style-prop-object */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import moment from 'moment';
import cx from 'classnames';
import { fromCents, getCurrencyCode } from 'utils/helpers';
import { getScrollTop, scrollTo } from 'utils/scrolling';
import { inOutQuintic } from 'utils/easing';

const propTypes = {
  transactions: PropTypes.array,
  filterMap: PropTypes.array,
};

class TransactionsMobile extends Component {
  constructor() {
    super();
    this.state = {
      isFilterSticky: false,
    };

    this.filterHeader = null;
    this.setFilterHeaderRef = (element) => {
      this.filterHeader = element;
    };
    this.filterHeaderPos = null;

    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);

    if (this.filterHeader) {
      // Save filter header position to help make it sticky / not sticky
      this.filterHeaderPos = this.filterHeader.getBoundingClientRect().top;
    }
  }

  componentDidUpdate() {
    // Scroll the filters to the right once the months are done rendering (>2 for the fade <spans>)
    if (this.filterHeader && this.filterHeader.children.length > 2) {
      this.filterHeader.scrollLeft = this.filterHeader.scrollWidth;

      // Focus the last month so it appears selected
      this.filterHeader.children[this.filterHeader.children.length - 2].focus();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  // eslint-disable-next-line react/sort-comp -- node upgrade
  getDetails(item) {
    if (item.type === 'ExtraService') {
      return {
        serviceName: <FormattedMessage id={`transactions.ExtraService.${item.description}`} />,
      };
    }
    return { accountNumber: item.description };
  }

  handleScroll() {
    // Sticky the header if it scrolls past the top of the screen
    if (getScrollTop() > this.filterHeaderPos && !this.state.isFilterSticky) {
      this.setState({ isFilterSticky: true });
    } else if (getScrollTop() < this.filterHeaderPos && this.state.isFilterSticky) {
      this.setState({ isFilterSticky: false });
    }
  }

  handleAnchorClick(e) {
    // Scroll the page to anchor
    scrollTo(document.getElementById(
      e.target.attributes['data-to'].value
    ).getBoundingClientRect().y + getScrollTop() - 64);

    // Center the filter on the one you clicked
    this.centerHorizontally(e.target);
  }

  centerHorizontally(eventTarget) {
    // Adapted from scrollTo in utils/scrolling.js for a very specific use case
    const parentNode = eventTarget.parentNode;

    // Calculate the scroll position we want to achieve
    const viewportOffset = Math.max(
      document.documentElement.clientWidth,
      window.innerWidth || 0
    ) / 2;
    const scrollTarget = eventTarget.offsetLeft - viewportOffset
      + (eventTarget.getBoundingClientRect().width / 2);

    // Tween the animation
    const start = parentNode.scrollLeft;
    const change = scrollTarget - start;
    let currentTime = 0;
    const increment = 20;
    const animationDuration = 500;
    const animateScroll = () => {
      currentTime += increment;
      const val = inOutQuintic(currentTime, start, change, animationDuration);
      parentNode.scrollLeft = val;
      if (currentTime < animationDuration) {
        window.requestAnimationFrame(animateScroll);
      }
    };
    window.requestAnimationFrame(animateScroll);
  }

  renderYearFilters(year) {
    return year.months.slice().reverse().map((month, index) => (
      <button
        className="transaction-filters__link"
        data-to={`${year.year}-${month}`}
        key={`filter-${year.year}-${month}`}
        onClick={(e) => this.handleAnchorClick(e)}
      >
        { moment().month(month).format('MMM') }
        { index === year.months.length - 1 ? ` ${moment().year(year.year).format('YYYY')}` : null }
      </button>
    ));
  }

  renderTransactions() {
    // Render transactions with monthly spacers inbetween
    const { transactions } = this.props;
    let needsSpacer;
    let yearString;
    let lastMonthApproved;
    let lastYearApproved = moment().year() + 1; // Start rendering years with current year

    return transactions.map((data, index) => {
      const dataDate = moment(data.created);
      if (dataDate.month() !== lastMonthApproved) {
        needsSpacer = true;
        lastMonthApproved = dataDate.month();
      } else {
        needsSpacer = false;
      }

      if (dataDate.year() < lastYearApproved) {
        yearString = ` ${dataDate.format('YYYY')}`;
        lastYearApproved = dataDate.year();
      } else {
        yearString = null;
      }

      return (
        <Fragment key={`transaction-${dataDate}-${index}`}>
          {needsSpacer
            && (
            <tr
              id={`${dataDate.year()}-${dataDate.month()}`}
              className="table__separator"
            >
              <td>
                {dataDate.format('MMMM')}
                {yearString}
              </td>
              <td />
            </tr>
            )}
          <tr>
            <td>
              {data.type !== 'Unknown'
                && (
                <FormattedMessage
                  id={`transactions.${data.type}.title`}
                  values={this.getDetails(data)}
                />
                )}
              <div className="txt-muted">
                {dataDate.format('L')}
              </div>
            </td>
            <td>
              <FormattedNumber
                style="currency"
                currency={getCurrencyCode()}
                value={fromCents(data.amount)}
              />
            </td>
          </tr>
        </Fragment>
      );
    });
  }

  render() {
    const { filterMap } = this.props;
    const filterClasses = cx({
      'transaction-filters': true,
      'transaction-filters--sticky': this.state.isFilterSticky,
    });
    const tableClasses = cx({
      table: true,
      'table--transactions': true,
      'table--mobile-transactions': true,
      'table--mobile-transactions--sticky': this.state.isFilterSticky,
    });

    return (
      <>
        <div className="content-block__full-child">
          <div ref={this.setFilterHeaderRef} className={filterClasses}>
            <div className="transaction-filters__fade" />
            {filterMap.map((year) => this.renderYearFilters(year))}
            <div className="transaction-filters__fade transaction-filters__fade--right" />
          </div>
        </div>
        <div className="content-block__full-child">
          <table className={tableClasses}>
            <tbody>
              {this.renderTransactions()}
            </tbody>
          </table>
        </div>
      </>
    );
  }
}

TransactionsMobile.propTypes = propTypes;

export default TransactionsMobile;
